export type PortabilidadeTipo = 'cnpj-cnpj' | 'cnpj-diferente' | 'pf-pj' | 'ambiguo';

export interface PortabilidadeRepresentanteFields {
  nome: string;
  cpf: string;
}

export interface PortabilidadeLineFields {
  id?: string;
  negociacao?: string;
  ehPortabilidade?: boolean;
  portabilidadeNomeCompleto?: string;
  portabilidadeCpf?: string;
  portabilidadeOperadora?: string;
  portabilidadeLinha?: string;
  /** CNPJ do cedente, quando a portabilidade é entre CNPJs diferentes (cnpj-diferente). */
  portabilidadeCnpjCedente?: string;
  /** Razão social do cedente, exigida junto com o CNPJ do cedente. */
  portabilidadeRazaoSocialCedente?: string;
  /** Representante legal do cedente (0..1 por linha). */
  portabilidadeRepresentantes?: PortabilidadeRepresentanteFields[];
  excluirDoContrato?: boolean;
}

const onlyDigits = (value?: string) => (value ?? '').replace(/\D/g, '');

export function resolvePortabilidadeTipo(linha: PortabilidadeLineFields): PortabilidadeTipo {
  const hasNome = !!(linha.portabilidadeNomeCompleto ?? '').trim();
  const hasCpf = onlyDigits(linha.portabilidadeCpf).length > 0;
  const hasCnpjCedente = onlyDigits(linha.portabilidadeCnpjCedente).length > 0;
  const hasRazaoSocial = !!(linha.portabilidadeRazaoSocialCedente ?? '').trim();

  const pfParcial = hasNome !== hasCpf;
  const cedenteParcial = hasCnpjCedente !== hasRazaoSocial;
  if (pfParcial || cedenteParcial) return 'ambiguo';

  const hasPf = hasNome && hasCpf;
  const hasCedente = hasCnpjCedente && hasRazaoSocial;
  if (hasPf && hasCedente) return 'ambiguo';
  if (hasPf) return 'pf-pj';
  if (hasCedente) return 'cnpj-diferente';
  return 'cnpj-cnpj';
}

export function getPortabilidadeTipoLabel(tipo: PortabilidadeTipo): string {
  if (tipo === 'pf-pj') return 'PF→CNPJ';
  if (tipo === 'cnpj-diferente') return 'CNPJ→CNPJ (cedente)';
  if (tipo === 'cnpj-cnpj') return 'Mesmo CNPJ';
  return 'Incompleto';
}

export function isPortabilidadeAditivoLine(linha: PortabilidadeLineFields): boolean {
  return (
    linha.negociacao === 'Aditivo' &&
    !!linha.ehPortabilidade &&
    !linha.excluirDoContrato
  );
}

export function getContratoPortabilidadeTipo(linhas: PortabilidadeLineFields[]): PortabilidadeTipo | null {
  let tipo: PortabilidadeTipo | null = null;
  for (const linha of linhas) {
    if (!isPortabilidadeAditivoLine(linha)) continue;
    const resolved = resolvePortabilidadeTipo(linha);
    if (resolved === 'ambiguo') continue;
    if (!tipo) tipo = resolved;
  }
  return tipo;
}

function normalizeRepresentanteUnico(
  representantes?: PortabilidadeRepresentanteFields[]
): PortabilidadeRepresentanteFields | null {
  const list = (representantes ?? [])
    .map((rep) => ({
      nome: (rep.nome ?? '').trim(),
      cpf: onlyDigits(rep.cpf),
    }))
    .filter((rep) => rep.nome.length > 0 || rep.cpf.length > 0);

  if (list.length === 0) return null;
  return list[0];
}

export function getPortabilidadeValidationError(linha: PortabilidadeLineFields): string | null {
  if (!isPortabilidadeAditivoLine(linha)) return null;

  const tipo = resolvePortabilidadeTipo(linha);
  if (tipo === 'ambiguo') {
    return 'Preencha nome e CPF juntos (PF→CNPJ) ou CNPJ e razão social do cedente juntos (CNPJ cedente diferente), sem misturar nem deixar parcial.';
  }

  if (!(linha.portabilidadeOperadora ?? '').trim()) {
    return 'Operadora de origem é obrigatória.';
  }

  if (onlyDigits(linha.portabilidadeLinha).length < 10) {
    return 'Linha a portar é obrigatória (DDD + número).';
  }

  if (tipo === 'cnpj-diferente') {
    const reps = linha.portabilidadeRepresentantes ?? [];
    if (reps.length > 1) {
      return 'Cada linha de portabilidade admite no máximo um representante legal.';
    }

    const rep = normalizeRepresentanteUnico(reps);
    if (!rep || !rep.nome || rep.cpf.length !== 11) {
      return 'CNPJ cedente diferente exige um representante legal (nome e CPF completo).';
    }
  }

  return null;
}

export function isPortabilidadeIncompleta(
  linha: PortabilidadeLineFields,
  _requirePortabilidadeCompleta = true
): boolean {
  return getPortabilidadeValidationError(linha) !== null;
}

/** Subconjunto copiável: só campos de titular/portabilidade (nunca negociacao, id, ehPortabilidade ou excluirDoContrato). */
export type PortabilidadeCopiedFields = Partial<
  Omit<PortabilidadeLineFields, 'id' | 'negociacao' | 'ehPortabilidade' | 'excluirDoContrato'>
>;

/** Campos de titular/portabilidade copiáveis de uma linha para outra (exceto linha a portar, por padrão). */
export function copyPortabilidadeFieldsFromLine(
  source: PortabilidadeLineFields,
  options?: { includeLinhaAPortar?: boolean }
): PortabilidadeCopiedFields {
  const copied: PortabilidadeCopiedFields = {
    portabilidadeNomeCompleto: source.portabilidadeNomeCompleto ?? '',
    portabilidadeCpf: source.portabilidadeCpf ?? '',
    portabilidadeOperadora: source.portabilidadeOperadora ?? '',
    portabilidadeCnpjCedente: source.portabilidadeCnpjCedente ?? '',
    portabilidadeRazaoSocialCedente: source.portabilidadeRazaoSocialCedente ?? '',
    portabilidadeRepresentantes: (source.portabilidadeRepresentantes ?? []).map((rep) => ({
      nome: rep.nome ?? '',
      cpf: rep.cpf ?? '',
    })),
  };
  if (options?.includeLinhaAPortar) {
    copied.portabilidadeLinha = source.portabilidadeLinha ?? '';
  }
  return copied;
}

/** Aviso não bloqueante quando há mais de um portante PF→CNPJ no contrato. */
export function getPortabilidadeMultiTitularInfo(linhas: PortabilidadeLineFields[]): string | null {
  const portantes = new Set<string>();
  for (const linha of linhas) {
    if (!isPortabilidadeAditivoLine(linha)) continue;
    if (resolvePortabilidadeTipo(linha) !== 'pf-pj') continue;
    const cpf = onlyDigits(linha.portabilidadeCpf);
    const nome = (linha.portabilidadeNomeCompleto ?? '').trim().toLowerCase();
    if (cpf.length === 11 && nome) {
      portantes.add(`${nome}|${cpf}`);
    }
  }
  if (portantes.size > 1) {
    return 'Há linhas PF→CNPJ com titulares diferentes neste contrato. Cada linha pode ter seu próprio titular; confira o termo/PDF de portabilidade gerado pela API.';
  }
  return null;
}

export function getPortanteCanonico(
  linhas: PortabilidadeLineFields[]
): { nome: string; cpf: string } | null {
  for (const linha of linhas) {
    if (!isPortabilidadeAditivoLine(linha)) continue;
    if (resolvePortabilidadeTipo(linha) !== 'pf-pj') continue;
    return {
      nome: (linha.portabilidadeNomeCompleto ?? '').trim(),
      cpf: onlyDigits(linha.portabilidadeCpf),
    };
  }
  return null;
}

export interface PortabilidadeCedenteCanonico {
  cnpj: string;
  razaoSocial: string;
  representantes: PortabilidadeRepresentanteFields[];
}

export function getCedenteCanonico(
  linhas: PortabilidadeLineFields[]
): PortabilidadeCedenteCanonico | null {
  for (const linha of linhas) {
    if (!isPortabilidadeAditivoLine(linha)) continue;
    if (resolvePortabilidadeTipo(linha) !== 'cnpj-diferente') continue;
    const rep = normalizeRepresentanteUnico(linha.portabilidadeRepresentantes);
    return {
      cnpj: onlyDigits(linha.portabilidadeCnpjCedente),
      razaoSocial: (linha.portabilidadeRazaoSocialCedente ?? '').trim(),
      representantes: rep ? [rep] : [],
    };
  }
  return null;
}

/** Representantes únicos (por CPF) já ligados a outras linhas de portabilidade do contrato. */
export function listRepresentantesReutilizaveis(
  linhas: PortabilidadeLineFields[],
  excludeId?: string
): PortabilidadeRepresentanteFields[] {
  const seen = new Set<string>();
  const result: PortabilidadeRepresentanteFields[] = [];

  for (const linha of linhas) {
    if (!isPortabilidadeAditivoLine(linha)) continue;
    if (excludeId && (linha.id === excludeId)) continue;
    if (resolvePortabilidadeTipo(linha) !== 'cnpj-diferente') continue;

    const rep = normalizeRepresentanteUnico(linha.portabilidadeRepresentantes);
    if (!rep || rep.cpf.length !== 11 || !rep.nome) continue;
    if (seen.has(rep.cpf)) continue;
    seen.add(rep.cpf);
    result.push({ nome: rep.nome, cpf: rep.cpf });
  }

  return result;
}

export interface PortabilidadeTipoConflict {
  conflict: true;
  kind: 'tipo';
  existingTipo: PortabilidadeTipo;
  newTipo: PortabilidadeTipo;
  affectedCount: number;
}

export interface PortabilidadePortanteConflict {
  conflict: true;
  kind: 'portante';
  canonical: { nome: string; cpf: string };
  newPortante: { nome: string; cpf: string };
}

export interface PortabilidadeCedenteConflict {
  conflict: true;
  kind: 'cedente';
  canonical: PortabilidadeCedenteCanonico;
  newCedente: PortabilidadeCedenteCanonico;
}

export type PortabilidadeConflict =
  | PortabilidadeTipoConflict
  | PortabilidadePortanteConflict
  | PortabilidadeCedenteConflict
  | { conflict: false };

export function detectPortabilidadeConflict(
  linhas: PortabilidadeLineFields[],
  linhaNovaOuEditada: PortabilidadeLineFields,
  editingId?: string
): PortabilidadeConflict {
  if (!isPortabilidadeAditivoLine(linhaNovaOuEditada)) {
    return { conflict: false };
  }

  const newTipo = resolvePortabilidadeTipo(linhaNovaOuEditada);
  if (newTipo === 'ambiguo') {
    return { conflict: false };
  }

  const others = linhas.filter(
    (l) =>
      isPortabilidadeAditivoLine(l) &&
      l.id !== editingId &&
      l.id !== linhaNovaOuEditada.id
  );

  const existingTipo = getContratoPortabilidadeTipo(others);
  if (existingTipo && existingTipo !== newTipo) {
    return {
      conflict: true,
      kind: 'tipo',
      existingTipo,
      newTipo,
      affectedCount: others.length,
    };
  }

  if (newTipo === 'cnpj-diferente') {
    const canonical = getCedenteCanonico(others);
    const newCedente: PortabilidadeCedenteCanonico = {
      cnpj: onlyDigits(linhaNovaOuEditada.portabilidadeCnpjCedente),
      razaoSocial: (linhaNovaOuEditada.portabilidadeRazaoSocialCedente ?? '').trim(),
      representantes: (() => {
        const rep = normalizeRepresentanteUnico(linhaNovaOuEditada.portabilidadeRepresentantes);
        return rep ? [rep] : [];
      })(),
    };

    if (canonical && canonical.cnpj !== newCedente.cnpj) {
      return {
        conflict: true,
        kind: 'cedente',
        canonical,
        newCedente,
      };
    }
  }

  return { conflict: false };
}

function takeFirstRep(
  reps?: PortabilidadeRepresentanteFields[]
): PortabilidadeRepresentanteFields[] {
  const rep = normalizeRepresentanteUnico(reps);
  return rep ? [{ nome: rep.nome, cpf: rep.cpf }] : [];
}

export function applyPortabilidadeTipoConversion<T extends PortabilidadeLineFields>(
  linhas: T[],
  targetTipo: PortabilidadeTipo,
  sourceLinha: PortabilidadeLineFields
): T[] {
  return linhas.map((linha) => {
    if (!isPortabilidadeAditivoLine(linha)) return linha;

    if (targetTipo === 'cnpj-cnpj') {
      return {
        ...linha,
        portabilidadeNomeCompleto: '',
        portabilidadeCpf: '',
        portabilidadeCnpjCedente: '',
        portabilidadeRazaoSocialCedente: '',
        portabilidadeRepresentantes: [],
      };
    }

    if (targetTipo === 'cnpj-diferente') {
      return {
        ...linha,
        portabilidadeNomeCompleto: '',
        portabilidadeCpf: '',
        portabilidadeCnpjCedente: sourceLinha.portabilidadeCnpjCedente ?? '',
        portabilidadeRazaoSocialCedente: sourceLinha.portabilidadeRazaoSocialCedente ?? '',
        portabilidadeRepresentantes: takeFirstRep(sourceLinha.portabilidadeRepresentantes),
      };
    }

    return {
      ...linha,
      portabilidadeNomeCompleto: sourceLinha.portabilidadeNomeCompleto ?? '',
      portabilidadeCpf: sourceLinha.portabilidadeCpf ?? '',
      portabilidadeCnpjCedente: '',
      portabilidadeRazaoSocialCedente: '',
      portabilidadeRepresentantes: [],
    };
  });
}

export function applyPortanteUnification<T extends PortabilidadeLineFields>(
  linhas: T[],
  sourceLinha: PortabilidadeLineFields
): T[] {
  const nome = (sourceLinha.portabilidadeNomeCompleto ?? '').trim();
  const cpf = sourceLinha.portabilidadeCpf ?? '';

  return linhas.map((linha) => {
    if (!isPortabilidadeAditivoLine(linha)) return linha;
    if (resolvePortabilidadeTipo(linha) !== 'pf-pj') return linha;

    return {
      ...linha,
      portabilidadeNomeCompleto: nome,
      portabilidadeCpf: cpf,
    };
  });
}

export function applyCedenteUnification<T extends PortabilidadeLineFields>(
  linhas: T[],
  sourceLinha: PortabilidadeLineFields
): T[] {
  const cnpjCedente = sourceLinha.portabilidadeCnpjCedente ?? '';
  const razaoSocialCedente = sourceLinha.portabilidadeRazaoSocialCedente ?? '';
  const representantes = takeFirstRep(sourceLinha.portabilidadeRepresentantes);

  return linhas.map((linha) => {
    if (!isPortabilidadeAditivoLine(linha)) return linha;
    if (resolvePortabilidadeTipo(linha) !== 'cnpj-diferente') return linha;

    return {
      ...linha,
      portabilidadeCnpjCedente: cnpjCedente,
      portabilidadeRazaoSocialCedente: razaoSocialCedente,
      portabilidadeRepresentantes: representantes.map((rep) => ({ ...rep })),
    };
  });
}

export function validateAllPortabilidadeLines(linhas: PortabilidadeLineFields[]): string | null {
  for (const linha of linhas) {
    const err = getPortabilidadeValidationError(linha);
    if (err) return err;
  }

  const portabilidadeLines = linhas.filter(isPortabilidadeAditivoLine);
  const tipos = new Set(
    portabilidadeLines
      .map(resolvePortabilidadeTipo)
      .filter((t) => t !== 'ambiguo')
  );
  if (tipos.size > 1) {
    return 'Todas as linhas de portabilidade devem ser do mesmo tipo (CNPJ→CNPJ, CNPJ cedente diferente ou PF→CNPJ).';
  }

  if (tipos.has('cnpj-diferente')) {
    const canonical = getCedenteCanonico(portabilidadeLines);
    if (canonical) {
      const cedenteLines = portabilidadeLines.filter(
        (l) => resolvePortabilidadeTipo(l) === 'cnpj-diferente'
      );

      const cnpjDivergente = cedenteLines.some(
        (l) => onlyDigits(l.portabilidadeCnpjCedente) !== canonical.cnpj
      );
      if (cnpjDivergente) {
        return 'Todas as linhas devem ter o mesmo cedente (CNPJ).';
      }
    }
  }

  return null;
}
